/**
 * Cutter-engine segment source for TAMS-backed items: maps the cutter's
 * zero-based item timeline onto a TAMS Flow and demuxes the flow's MPEG-TS
 * segments directly into the packets the cutter decodes. This is the cutter
 * counterpart of the MSE player's tamsCmafSegmentSource, minus the CMAF
 * transmuxing step the cutter does not need.
 *
 * Non-uniform by construction: `getSegmentInfoAt(position)` issues a timerange
 * point query and returns the segment's real `start`/`end`/`id` (its raw
 * timerange string), so segments of any duration play back correctly. All store
 * access goes through the backend-proxied {@link TamsClient} — the store token
 * stays in the backend; the browser only calls the item-scoped Mimir segment
 * endpoint. See `tams-nonuniform-playback-plan.md`.
 */
import type { Fraction } from '@mimir/common/util/fractionType';
import type { PlayableArtifactTams } from '@mimir/common/dto/playableItemDto';
import type { SegmentInfo } from '@mimir/common/types/segmentProvider';
import type { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import type { Clock } from '@mimir/common/types/clock';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import type { FuzzyBounds, FuzzyValue } from '@mimir/common/util/fuzzyNumbers';
import type { BackendTamsSegmentFetcher } from '@mimir/common/tams/backendProxiedTamsClient';
import { createBackendProxiedTamsClient } from '@mimir/common/tams/backendProxiedTamsClient';
import { findTamsFlowRange, parseTamsTimerange, selectTamsSegmentUrl, serializeTamsPointQuery } from '@mimir/common/tams/utils';
import { addFractions, compareFractions, subtractFractions } from '@mimir/common/util/integerMath';
import { fractionToFuzzy } from '@mimir/common/util/fuzzyMath';
import { invariant } from '@mimir/common/util/invariant';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { resilientFetch } from '@mimir/common/io/resilientFetch';
import { isRetryableSegmentResponse } from '@mimir/common/util/isRetryableSegmentResponse';

/**
 * The TAMS-specific subset of the cutter {@link import('@mimir/common/types/segmentProvider').SegmentProvider}
 * contract. Segment ids are the raw TAMS segment timerange strings, so
 * {@link getSegment} can re-resolve the segment (and its fresh pre-signed
 * URLs) statelessly.
 */
export interface TamsSegmentSource {
  getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null>;
  getSegment(id: string): Promise<EncodedData>;
  getDuration(): Promise<Fraction>;
}

export interface TamsSegmentSourceServices {
  apiActions: VideoApiActions;
  fetch: BasicFetch;
  logger: Logger;
  clock: Clock;
  demuxTs: (bytes: Uint8Array, firstSegment: boolean) => Promise<EncodedData>;
}

// TAMS timeranges are exact `seconds:nanoseconds` rationals, so parsing yields
// precise fractions; the cutter timeline arithmetic requires exact positions.
function preciseFraction(value: FuzzyValue, what: string): Fraction {
  invariant(value.precise !== undefined, () => `${what} has no exact rational value`);
  return value.precise;
}

export function createTamsSegmentSource(
  services: TamsSegmentSourceServices,
  itemId: string,
  artifact: PlayableArtifactTams,
): TamsSegmentSource {
  const { apiActions, fetch, logger, clock, demuxTs } = services;

  const flowId = artifact.proxyFlowIds[0] ?? artifact.highresFlowIds[0];
  if (flowId === undefined) {
    throw new Error('TAMS artifact has no flows');
  }
  if (artifact.proxyFlowIds.length > 1) {
    // Mimir producers emit a single muxed-TS flow per playback quality, so
    // this only triggers for external stores exposing separate per-essence
    // flows, which the cutter cannot merge into one packet stream.
    logger.warn(`TAMS artifact has ${artifact.proxyFlowIds.length} proxy flows; only the first (${flowId}) will be played`);
  }

  // All store access is proxied through the item-scoped Mimir segment endpoint;
  // the store OAuth token never reaches the browser.
  const fetcher: BackendTamsSegmentFetcher = async (parameters) => {
    const response = await apiActions.getTamsSegments(itemId, parameters);
    return response.segments;
  };
  const tamsClient = createBackendProxiedTamsClient(fetcher, artifact);

  const segmentFetch = resilientFetch(
    { fetch, AbortController },
    { shouldRetry: isRetryableSegmentResponse, retryDelayLimit: 30_000 },
    clock,
    logger,
  );

  // The flow range positions the cutter's zero-based timeline within the TAMS
  // timeline: logical position = TAMS time - range start. Mimir-produced flows
  // start at 0; external flows may be anchored anywhere (e.g. wall clock).
  let rangePromise: Promise<FuzzyBounds> | null = null;
  let lastKnownRange: FuzzyBounds | null = null;

  const getFlowRange = (refresh: boolean): Promise<FuzzyBounds> => {
    if (rangePromise === null || refresh) {
      rangePromise = findTamsFlowRange(logger, tamsClient, flowId, lastKnownRange)
        .then((range) => {
          lastKnownRange = range;
          return range;
        })
        .catch((error) => {
          rangePromise = null;
          throw error;
        });
    }
    return rangePromise;
  };

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const range = await getFlowRange(false);
      const rangeStart = preciseFraction(range.start, `Flow ${flowId} range start`);
      const rangeEnd = preciseFraction(range.end, `Flow ${flowId} range end`);

      const absolutePosition = addFractions(position, rangeStart);
      if (compareFractions(absolutePosition, rangeEnd) >= 0) {
        return null;
      }

      // A point query: TAMS returns the segment whose (start-inclusive,
      // end-exclusive) timerange contains this instant. A genuine
      // [position, position + epsilon) range, not a zero-width point — at
      // Unix-epoch magnitudes, converting the exact `absolutePosition`
      // fraction into `FuzzyValue.approximate` (a JS double) can round by
      // tens to hundreds of nanoseconds, which a zero-width query has no
      // tolerance for. Callers that land exactly on a boundary shared with a
      // neighboring segment (e.g. `populateCacheInProximityOf`'s
      // `minSegmentSize` nudge) already nudge clear of it before calling in,
      // so a forward-tolerant range is safe here unconditionally.
      const timerange = serializeTamsPointQuery(fractionToFuzzy(absolutePosition));
      const [segment] = await tamsClient.getSegments({ flowId, timerange, limit: 1 });
      if (segment === undefined) {
        return null;
      }

      const segmentRange = parseTamsTimerange(segment.timerange);
      return {
        id: segment.timerange,
        start: subtractFractions(preciseFraction(segmentRange.start, `Segment ${segment.timerange} start`), rangeStart),
        end: subtractFractions(preciseFraction(segmentRange.end, `Segment ${segment.timerange} end`), rangeStart),
      };
    },

    async getSegment(id: string): Promise<EncodedData> {
      // Re-resolve the segment by its timerange id instead of caching it from
      // getSegmentInfoAt(): pre-signed URLs expire, and the packet cache may
      // re-fetch a segment long after its info was first looked up.
      const [segment] = await tamsClient.getSegments({ flowId, timerange: id, limit: 1 });
      if (segment === undefined || segment.timerange !== id) {
        throw new Error(`TAMS segment ${id} no longer exists on flow ${flowId}`);
      }

      const url = selectTamsSegmentUrl(segment);
      const response = await segmentFetch(url, { });
      if (!response.ok || response.body === null) {
        throw new Error(`Failed to fetch TAMS segment: ${response.status} ${response.statusText}`);
      }
      const bytes = await readWholeStream(response.body);
      return await demuxTs(bytes, false);
    },

    async getDuration(): Promise<Fraction> {
      const range = await getFlowRange(true);
      return subtractFractions(
        preciseFraction(range.end, `Flow ${flowId} range end`),
        preciseFraction(range.start, `Flow ${flowId} range start`),
      );
    },
  };
}
